import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_DEPLOYMENT_ENVIRONMENT } from '@opentelemetry/semantic-conventions';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { diag, DiagConsoleLogger, DiagLogLevel } from '@opentelemetry/api';
import { SeverityNumber, logs, type AnyValueMap } from '@opentelemetry/api-logs';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { environment } from '../environments/environment';

const rawEndpoint = environment.otelOtlpEndpoint ?? 'http://localhost:4318';
const baseEndpoint = rawEndpoint.replace(/\/?v1\/(traces|metrics|logs)$/, '').replace(/\/+$/, '');

const otelTracesEnabled = environment.otelTracesEnabled ?? true;
const otelMetricsEnabled = environment.otelMetricsEnabled ?? true;
const otelLogsEnabled = environment.otelLogsEnabled ?? true;
const anyOtelEnabled = otelTracesEnabled || otelMetricsEnabled || otelLogsEnabled;

const readToken = (): string | undefined => {
    const token = localStorage.getItem('token') ?? localStorage.getItem('token-client');
    return token ?? undefined;
};

const CLIENT_ID_STORAGE_KEY = 'otel_client_id';

const createClientId = (): string => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        return (crypto as any)?.randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    } catch {
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
};

export const getOrCreateClientId = (): string => {
    const existing = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (existing) return existing;
    const id = createClientId();
    localStorage.setItem(CLIENT_ID_STORAGE_KEY, id);
    return id;
};

const parseJwtPayload = (jwt: string): any | undefined => {
    const parts = jwt.split('.');
    if (parts.length < 2) return undefined;
    try {
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(b64)
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(''),
        );
        return JSON.parse(json);
    } catch {
        return undefined;
    }
};

const getEndUserAttributes = (): Record<string, string> => {
    const token = readToken();
    if (!token) return {};

    const payload = parseJwtPayload(token);
    const currentUser = payload?.currentUser?.data;
    if (!currentUser) return {};

    const id = currentUser?.id ? String(currentUser.id) : undefined;
    const name = currentUser?.name
        ? currentUser?.lastName
            ? `${String(currentUser.name)} ${String(currentUser.lastName)}`
            : String(currentUser.name)
        : undefined;
    const attrs: Record<string, string> = {};
    if (id) attrs['enduser.id'] = id;
    if (name) attrs['enduser.name'] = name;
    return attrs;
};

export const getFrontendContextAttributes = (): Record<string, string> => {
    return {
        client_id: getOrCreateClientId(),
        ...getEndUserAttributes(),
    };
};

const originalSendBeacon = navigator.sendBeacon?.bind(navigator);
if (anyOtelEnabled && originalSendBeacon) {
    navigator.sendBeacon = ((url: string | URL, data?: BodyInit | null) => {
        const urlStr = typeof url === 'string' ? url : url.toString();
        if (urlStr.includes('/v1/metrics') || urlStr.includes('/v1/logs') || urlStr.includes('/v1/traces')) {
            const headers: Record<string, string> = {};
            if (data instanceof Blob && data.type) {
                headers['Content-Type'] = data.type;
            }
            void fetch(urlStr, {
                method: 'POST',
                body: data ?? null,
                keepalive: true,
                mode: 'cors',
                credentials: 'omit',
                headers: Object.keys(headers).length ? headers : undefined,
            }).catch(() => {
                // ignore
            });
            return true;
        }
        return originalSendBeacon(url as any, data as any);
    }) as any;
}

if ((import.meta as any).env?.MODE !== 'production') {
    diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.DEBUG);
}

// 2. Configurar exportadores OTLP (hacia tu OpenTelemetry Collector)
const exporter = otelTracesEnabled
    ? new OTLPTraceExporter({
          url: `${baseEndpoint}/v1/traces`,
      })
    : undefined;

const logExporter = otelLogsEnabled
    ? new OTLPLogExporter({
          url: `${baseEndpoint}/v1/logs`,
      })
    : undefined;

const metricExporter = otelMetricsEnabled
    ? new OTLPMetricExporter({
          url: `${baseEndpoint}/v1/metrics`,
          headers: {
              'Content-Type': 'application/json',
          },
          timeoutMillis: environment.otelExporterTimeoutMillis ?? 10000,
      })
    : undefined;

// 3. Crear el proveedor de trazadores (Tracer Provider)
if (otelTracesEnabled && exporter) {
    const provider = new WebTracerProvider({
        resource: resourceFromAttributes({
            [SEMRESATTRS_SERVICE_NAME]: 'lims-frontend',
            [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: (import.meta as any).env?.MODE ?? 'development',
            'client': environment.client || 'default',
            'client.name': environment.client || 'default',
        }),

        spanProcessors: [new BatchSpanProcessor(exporter)],
    });

    provider.register({
        contextManager: new ZoneContextManager(), // Permite la propagación del contexto con Zone.js
        propagator: new W3CTraceContextPropagator(),
    });
}

if (otelLogsEnabled && logExporter) {
    const loggerProvider = new LoggerProvider({
        resource: resourceFromAttributes({
            [SEMRESATTRS_SERVICE_NAME]: 'lims-frontend',
            [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: (import.meta as any).env?.MODE ?? 'development',
            'client': environment.client || 'default',
            'client.name': environment.client || 'default',
        }),
        processors: [new BatchLogRecordProcessor(logExporter)],
    });
    logs.setGlobalLoggerProvider(loggerProvider);
}

let meter: ReturnType<MeterProvider['getMeter']> | undefined;
let uiEventsCounter: ReturnType<ReturnType<MeterProvider['getMeter']>['createCounter']> | undefined;
let httpRequestsCounter: ReturnType<ReturnType<MeterProvider['getMeter']>['createCounter']> | undefined;
let httpRequestDurationMs: ReturnType<ReturnType<MeterProvider['getMeter']>['createHistogram']> | undefined;
let routeChangesCounter: ReturnType<ReturnType<MeterProvider['getMeter']>['createCounter']> | undefined;

if (otelMetricsEnabled && metricExporter) {
    const metricReader = new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: environment.otelMetricsExportIntervalMillis
            ? environment.otelMetricsExportIntervalMillis
            : ((import.meta as any).env?.MODE ?? 'development') === 'production'
              ? 60000
              : 30000,
    });

    const meterProvider = new MeterProvider({
        resource: resourceFromAttributes({
            [SEMRESATTRS_SERVICE_NAME]: 'lims-frontend',
            [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: (import.meta as any).env?.MODE ?? 'development',
            'client': environment.client || 'default',
            'client.name': environment.client || 'default',
        }),
        readers: [metricReader],
    });

    meter = meterProvider.getMeter('frontend');
    uiEventsCounter = meter.createCounter('frontend_ui_events_total', {
        description: 'Total UI events emitted by frontend logger',
    });

    httpRequestsCounter = meter.createCounter('frontend_http_requests_total', {
        description: 'Total HTTP requests initiated by the frontend (fetch)',
    });

    httpRequestDurationMs = meter.createHistogram('frontend_http_request_duration_ms', {
        description: 'HTTP request duration in milliseconds (fetch)',
        unit: 'ms',
    });

    routeChangesCounter = meter.createCounter('frontend_route_changes_total', {
        description: 'Total route changes detected in the browser (history/navigation)',
    });
}

const getPathLabel = (url: string): string => {
    try {
        const u = new URL(url, window.location.origin);
        return u.pathname || '/';
    } catch {
        const base = String(url);
        return base.split('?')[0]?.split('#')[0] || '/';
    }
};

const originalFetch = window.fetch.bind(window);
if (otelMetricsEnabled && httpRequestsCounter && httpRequestDurationMs) {
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const start = performance.now();
        const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
        const rawUrl = input instanceof Request ? input.url : String(input);
        const path = getPathLabel(rawUrl);

        try {
            const res = await originalFetch(input as any, init);
            const durationMs = performance.now() - start;
            const status = res.status;
            const statusClass = `${Math.floor(status / 100)}xx`;

            httpRequestsCounter.add(1, { method, path, status: String(status), status_class: statusClass, outcome: 'success' });
            httpRequestDurationMs.record(durationMs, {
                method,
                path,
                status: String(status),
                status_class: statusClass,
                outcome: 'success',
            });

            return res;
        } catch (e) {
            const durationMs = performance.now() - start;
            httpRequestsCounter.add(1, { method, path, status: '0', status_class: '0xx', outcome: 'error' });
            httpRequestDurationMs.record(durationMs, { method, path, status: '0', status_class: '0xx', outcome: 'error' });
            throw e;
        }
    };
}

const recordRouteChange = (navigationType: string) => {
    routeChangesCounter?.add(1, { path: window.location.pathname || '/', navigation_type: navigationType });
};

const originalPushState = history.pushState.bind(history);
if (otelMetricsEnabled && routeChangesCounter) {
    history.pushState = (data: any, unused: string, url?: string | URL | null) => {
        const res = originalPushState(data, unused, url as any);
        recordRouteChange('pushState');
        return res;
    };
}

const originalReplaceState = history.replaceState.bind(history);
if (otelMetricsEnabled && routeChangesCounter) {
    history.replaceState = (data: any, unused: string, url?: string | URL | null) => {
        const res = originalReplaceState(data, unused, url as any);
        recordRouteChange('replaceState');
        return res;
    };

    window.addEventListener('popstate', () => recordRouteChange('popstate'));
    window.addEventListener('hashchange', () => recordRouteChange('hashchange'));

    recordRouteChange('load');
}

const noopFrontendLogger = {
    info: (_message: string, _attributes?: AnyValueMap) => undefined,
    warn: (_message: string, _attributes?: AnyValueMap) => undefined,
    error: (_message: string, _attributes?: AnyValueMap) => undefined,
};

const otelLogger = otelLogsEnabled ? logs.getLogger('frontend') : undefined;
export const frontendLogger = otelLogger
    ? {
          info: (message: string, attributes?: AnyValueMap) =>
              (uiEventsCounter?.add(1, { severity: 'info' }),
              otelLogger.emit({ severityNumber: SeverityNumber.INFO, severityText: 'INFO', body: message, attributes })),
          warn: (message: string, attributes?: AnyValueMap) =>
              (uiEventsCounter?.add(1, { severity: 'warn' }),
              otelLogger.emit({ severityNumber: SeverityNumber.WARN, severityText: 'WARN', body: message, attributes })),
          error: (message: string, attributes?: AnyValueMap) =>
              (uiEventsCounter?.add(1, { severity: 'error' }),
              otelLogger.emit({ severityNumber: SeverityNumber.ERROR, severityText: 'ERROR', body: message, attributes })),
      }
    : noopFrontendLogger;

if (otelLogsEnabled) {
    window.addEventListener('error', (event) => {
        const err = (event as ErrorEvent).error as unknown;
        const error = err instanceof Error ? err : undefined;

        frontendLogger.error('frontend_unhandled_error', {
            message: (event as ErrorEvent).message,
            filename: (event as ErrorEvent).filename,
            lineno: (event as ErrorEvent).lineno,
            colno: (event as ErrorEvent).colno,
            stack: error?.stack,
            name: error?.name,
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = (event as PromiseRejectionEvent).reason as unknown;
        const error = reason instanceof Error ? reason : undefined;

        frontendLogger.error('frontend_unhandled_rejection', {
            reason: typeof reason === 'string' ? reason : undefined,
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
        });
    });
}

// 6. Registrar instrumentaciones automáticas
if (otelTracesEnabled) {
    registerInstrumentations({
        instrumentations: [
            getWebAutoInstrumentations({
                // Personaliza qué instrumentaciones se activan
                '@opentelemetry/instrumentation-fetch': {
                    enabled: true,
                    propagateTraceHeaderCorsUrls: /.*/, // Propaga headers en peticiones CORS
                    clearTimingResources: true,
                    applyCustomAttributesOnSpan: (span: any) => {
                        const attrs = getEndUserAttributes();
                        if (Object.keys(attrs).length) span.setAttributes(attrs);
                        span.setAttribute('client', environment.client || 'default');
                        span.setAttribute('client.name', environment.client || 'default');
                    },
                },
                '@opentelemetry/instrumentation-xml-http-request': {
                    enabled: true,
                    propagateTraceHeaderCorsUrls: /.*/,
                    applyCustomAttributesOnSpan: (span: any) => {
                        const attrs = getEndUserAttributes();
                        if (Object.keys(attrs).length) span.setAttributes(attrs);
                        span.setAttribute('client', environment.client || 'default');
                        span.setAttribute('client.name', environment.client || 'default');
                    },
                },
                '@opentelemetry/instrumentation-document-load': {
                    enabled: true,
                },
                '@opentelemetry/instrumentation-user-interaction': {
                    enabled: false,
                },
            }),
        ],
    });
}